export const esc = (s: unknown) =>
  String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

export function qs<T extends Element = HTMLElement>(root: ParentNode, sel: string): T {
  const el = root.querySelector<T>(sel);
  if (!el) throw new Error('missing element ' + sel);
  return el;
}

export const LOGO = `<svg viewBox="0 0 24 24" aria-hidden="true"><rect x="2" y="2" width="20" height="20" rx="3" fill="var(--accent)"/><path d="M8 5v5M8 14v5M8 14 5 9M14 6h5M14 6v12h5" stroke="var(--accent-ink)" stroke-width="1.6" fill="none" stroke-linecap="round"/></svg>`;

export function toast(text: string) {
  const d = document.createElement('div');
  d.className = 'toast';
  d.setAttribute('role', 'status');
  d.textContent = text;
  document.body.appendChild(d);
  setTimeout(() => d.remove(), 2000);
}

/** Opens a modal; buttons with data-m="<action>" call onAction. Returns a close function. */
export function openModal(html: string, onAction: (action: string, dlg: HTMLElement) => void): () => void {
  const wrap = document.createElement('div');
  wrap.className = 'modal';
  wrap.innerHTML = `<div class="dlg" role="dialog" aria-modal="true">${html}</div>`;
  const dlg = wrap.firstElementChild as HTMLElement;
  const close = () => {
    wrap.remove();
    document.removeEventListener('keydown', onKey, true);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      close();
    }
  };
  wrap.addEventListener('click', (e) => {
    if (e.target === wrap) return close();
    const a = (e.target as HTMLElement).closest<HTMLElement>('[data-m]')?.dataset.m;
    if (!a) return;
    if (a === 'close') return close();
    onAction(a, dlg);
  });
  document.addEventListener('keydown', onKey, true);
  document.body.appendChild(wrap);
  dlg.querySelector<HTMLElement>('input,textarea,button.pri,button')?.focus();
  return close;
}

export function downloadFile(name: string, content: string | Uint8Array, type: string) {
  const url = URL.createObjectURL(new Blob([content as BlobPart], { type }));
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const safeFileName = (s: string) => s.replace(/[\\/:*?"<>|]+/g, '_').trim() || 'proje';

export function readFileText(file: File): Promise<string> {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result));
    r.onerror = () => rej(r.error);
    r.readAsText(file);
  });
}

export function fmtDate(sqlUtc: string) {
  // the server's "YYYY-MM-DD HH:MM:SS" (UTC), or an ISO time with its zone (local projects)
  const d = new Date(/T.*(Z|[+-]\d\d:?\d\d)$/.test(sqlUtc) ? sqlUtc : sqlUtc.replace(' ', 'T') + 'Z');
  return isNaN(+d) ? sqlUtc : d.toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' });
}

/**
 * After a deploy an open tab still asks for the old lazily loaded chunks (3D view, STEP import),
 * which no longer exist. Such an error reloads the page once to pick up the new build.
 */
export function reloadIfStale(e: unknown): boolean {
  const msg = String((e as Error)?.message ?? e);
  if (!/dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(msg)) return false;
  try {
    const last = Number(sessionStorage.getItem('enercad.staleReload') || 0);
    if (Date.now() - last < 30_000) return false;
    sessionStorage.setItem('enercad.staleReload', String(Date.now()));
  } catch {
    return false;
  }
  location.reload();
  return true;
}
